import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Pager, Empty, BRANCH_LABEL, SortTh } from "../components/common";
import { OwnerFilter, TransferBar, useAccountOwners } from "../components/AccountTransfer";

const SEGMENTS = ["Corporate", "SMB", "Agency", "D2C"];
const COLS = [["account_id", "Account"], ["name", "Name"], ["type", "Segment"], ["branch", "Branch"], ["owner", "Owner"], ["tier", "Tier (BP category)"], [null, "Leads"], [null, "Won"]];
const sel = "bg-muted/60 border h-9 px-2 text-sm";

const Check = ({ on, onChange, tid, label }) => (
  <input type="checkbox" checked={on} onChange={onChange} onClick={(e) => e.stopPropagation()} data-testid={tid} aria-label={label} className="h-4 w-4 accent-[hsl(var(--primary))]" />
);

const useSelection = (ids) => {
  const [picked, setPicked] = useState([]);
  const allOn = ids.length > 0 && ids.every((i) => picked.includes(i));
  const flip = (i) => setPicked(picked.includes(i) ? picked.filter((x) => x !== i) : [...picked, i]);
  const flipAll = () => setPicked(allOn ? picked.filter((x) => !ids.includes(x)) : [...new Set([...picked, ...ids])]);
  return { picked, allOn, flip, flipAll, clear: () => setPicked([]) };
};

const useSortParams = (sp, setSp) => {
  const key = sp.get("sort") || "name";
  const order = Number(sp.get("order") || 1);
  const onSort = (k) => { const n = new URLSearchParams(sp); n.set("sort", k); n.set("order", String(key === k ? -order : 1)); n.delete("page"); setSp(n); };
  return { key, order, onSort };
};

const Filters = ({ sp, set, founder, owners, total }) => (
  <div className="flex flex-wrap gap-2 mb-4">
    <input data-testid="accounts-search" defaultValue={sp.get("q") || ""} placeholder="Search company & press Enter" onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)} className={`${sel} w-full max-w-sm`} />
    <select data-testid="accounts-filter-segment" value={sp.get("type") || ""} onChange={(e) => set("type", e.target.value)} className={sel}>
      <option value="">All segments</option>{SEGMENTS.map((s) => <option key={s}>{s}</option>)}<option value="none">No segment</option></select>
    <select data-testid="accounts-filter-branch" value={sp.get("branch") || ""} onChange={(e) => set("branch", e.target.value)} className={sel}>
      <option value="">All branches</option><option value="DEL">Delhi (FBD)</option><option value="MUM">Mumbai</option></select>
    {founder && <OwnerFilter owners={owners} value={sp.get("owner") || ""} onChange={(v) => set("owner", v)} />}
    <span className="text-xs text-muted-foreground self-center num" data-testid="accounts-total">{total} accounts</span>
  </div>
);

const AccountsTable = ({ items, founder, pick, sort }) => {
  const nav = useNavigate();
  return (
    <div className="ax-panel ax-scroll ax-freeze no-select"><table className="ax-table w-full min-w-[800px]" data-testid="accounts-table">
      <thead><tr>{founder && <th className="w-8"><Check on={pick.allOn} onChange={pick.flipAll} tid="accounts-check-all" label="Select all on this page" /></th>}{COLS.map(([k, l]) => <SortTh key={l} k={k} label={l} sort={sort} />)}</tr></thead>
      <tbody>{items.map((a) => (
        <tr key={a.account_id} className="cursor-pointer" onClick={() => nav(`/sales/accounts/${a.account_id}`)} data-testid={`account-row-${a.account_id}`}>
          {founder && <td onClick={(e) => e.stopPropagation()}><Check on={pick.picked.includes(a.account_id)} onChange={() => pick.flip(a.account_id)} tid={`account-check-${a.account_id}`} label={`Select ${a.name}`} /></td>}
          <td className="font-mono text-xs text-primary">{a.account_id}</td><td>{a.name}</td><td>{a.type || "—"}</td><td>{BRANCH_LABEL[a.branch] || a.branch}</td>
          <td>{a.owner || "—"}</td><td>{a.tier || "—"}</td><td className="num">{a.lead_count}</td><td className="num text-emerald-400">{a.won_count}</td></tr>
      ))}</tbody></table></div>
  );
};

export default function Accounts() {
  const { user } = useAuth();
  const founder = user?.role_key === "founder";
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState({ items: [], total: 0 });
  const [tick, setTick] = useState(0);
  const { owners, team } = useAccountOwners(founder, tick);
  const pick = useSelection(d.items.map((a) => a.account_id));
  const sort = useSortParams(sp, setSp);
  const key = sp.toString();
  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };
  const load = useCallback(() => api.get("/accounts", { params: Object.fromEntries(new URLSearchParams(key)) }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [key]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow="Sales & Leads" title="Accounts" desc={`Companies built from your Sales Tracker. Leads from the same company (matched by normalised name) share one account, which can hold several contacts.${founder ? " Tick accounts to transfer them, for example everything a leaver owned." : ""}`} />
      <Filters sp={sp} set={set} founder={founder} owners={owners} total={d.total} />
      {founder && <TransferBar ids={pick.picked} team={team} onClear={pick.clear} onDone={() => { pick.clear(); setTick((x) => x + 1); load(); }} />}
      {!d.items.length ? <Empty text="No accounts match." /> : <AccountsTable items={d.items} founder={founder} pick={pick} sort={sort} />}
      <Pager page={d.page || 1} total={d.total} size={50} onPage={(p) => set("page", String(p))} />
    </div>
  );
}
